import { KanbanBoard as SharedKanbanBoard } from "@iq/models";
import { useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import CreateTaskModal from "../modals/CreateTaskModal";
import ViewTaskModal from "../modals/ViewTaskModal";
import "./KanbanBoard.css";

const PM_COLUMNS = [
	{ id: "can_wait", title: "Can Wait", color: "var(--color-surface-soft)" },
	{ id: "not_started", title: "Not Started", color: "var(--color-action-primary)" },
	{ id: "working_on_it", title: "Working On It", color: "var(--color-warning)" },
	{ id: "testing", title: "Testing", color: "var(--color-danger)" },
	{ id: "done", title: "Done", color: "var(--color-success)" },
];

const PM_COLUMN_IDS = new Set(PM_COLUMNS.map((column) => column.id));

function normalizeTaskStatus(status) {
	const value = String(status || "").trim().toLowerCase();
	return PM_COLUMN_IDS.has(value) ? value : "not_started";
}

function formatDueDateLabel(value) {
	if (!value) {
		return "";
	}

	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) {
		return "";
	}

	return parsedDate.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function normalizePriorityTone(priority) {
	const normalized = String(priority || "medium")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");

	if (normalized === "urgent") {
		return "urgent";
	}

	if (normalized === "high") {
		return "high";
	}

	if (normalized === "low") {
		return "low";
	}

	return "medium";
}

function buildTaskBoardCard(task) {
	const priorityTone = normalizePriorityTone(task?.priority);
	const normalizedPriority = String(task?.priority || "medium").replace(/_/g, " ").trim() || "medium";

	return {
		title: String(task?.title || "Untitled task").trim() || "Untitled task",
		priorityLabel: normalizedPriority,
		priorityTone,
		assigneeLabel: String(
			task?.assigned_user?.name ??
			task?.assignedUser?.name ??
			task?.assignee_name ??
			task?.assigneeName ??
			"Unassigned"
		).trim() || "Unassigned",
		dueDateLabel: formatDueDateLabel(task?.due_date || task?.dueDate),
	};
}

function KanbanBoardAdapter({
	tasks = [],
	onMove,
	projectId,
	onTaskCreated,
	onTaskUpdated,
}) {
	const [createTaskOpen, setCreateTaskOpen] = useState(false);
	const [createTaskStatus, setCreateTaskStatus] = useState("not_started");
	const [selectedTask, setSelectedTask] = useState(null);

	const columnHeaderAction = useMemo(
		() => (column) => ({
			icon: <FiPlus size={16} />,
			ariaLabel: `Create task in ${column?.title || "column"}`,
			title: `Create task in ${column?.title || "column"}`,
			disabled: !projectId,
			onClick: () => {
				setCreateTaskStatus(column?.id || "not_started");
				setCreateTaskOpen(true);
			},
		}),
		[projectId]
	);

	return (
		<>
			<SharedKanbanBoard
				boardType="pm"
				items={(Array.isArray(tasks) ? tasks : []).map((task) => ({
					...task,
					boardCard: buildTaskBoardCard(task),
				}))}
				columns={PM_COLUMNS}
				getItemId={(task) => task?.id}
				getColumnIdForItem={(task) =>
					normalizeTaskStatus(task?.status || task?.task_status || task?.taskStatus)
				}
				onMove={onMove}
				onMoveError={(error, move) => {
					console.error("Failed to persist PM kanban move", { error, move });
				}}
				renderCard={({ item }) => {
					const boardCard = item?.boardCard || {};
					return (
						<>
							<div className="pm-kanban-card-header">
								<strong className="pm-kanban-card-title">{boardCard.title || "Untitled task"}</strong>
								{boardCard.priorityLabel ? (
									<div className={`pm-kanban-card-priority pm-kanban-card-priority--${boardCard.priorityTone || "medium"}`}>
										{boardCard.priorityLabel}
									</div>
								) : null}
							</div>
							<div className="pm-kanban-card-meta">
								<span className="pm-kanban-card-meta-label">Assigned To:</span>{" "}
								<span>{boardCard.assigneeLabel || "Unassigned"}</span>
							</div>
							{boardCard.dueDateLabel ? (
								<div className="pm-kanban-card-meta">
									<span className="pm-kanban-card-meta-label">Due:</span>{" "}
									<span>{boardCard.dueDateLabel}</span>
								</div>
							) : null}
						</>
					);
				}}
				onCardClick={(item) => {
					setSelectedTask(item || null);
				}}
				getColumnHeaderAction={columnHeaderAction}
			/>

			<CreateTaskModal
				isOpen={createTaskOpen}
				onClose={() => setCreateTaskOpen(false)}
				projectId={projectId}
				initialStatus={createTaskStatus}
				onCreated={onTaskCreated}
			/>

			<ViewTaskModal
				isOpen={Boolean(selectedTask)}
				onClose={() => setSelectedTask(null)}
				task={selectedTask}
				projectId={projectId}
				onTaskUpdated={async (updatedTask) => {
					setSelectedTask((prev) => updatedTask || prev);
					if (typeof onTaskUpdated === "function") {
						await onTaskUpdated(updatedTask);
					}
				}}
				onTaskDeleted={async () => {
					setSelectedTask(null);
					if (typeof onTaskUpdated === "function") {
						await onTaskUpdated();
					}
				}}
			/>
		</>
	);
}

export default KanbanBoardAdapter;

